import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRealKmClient } from '../../lib/useRealKmClient.js';
import { statoRisorsaLabel, invioRecente, giorniDallInvio, SOGLIA_INVIO_RECENTE_GIORNI } from './kmLabels.js';
import { KM_CONFIG } from '../../api/config.js';
import ResourceProfiloSa from './ResourceProfiloSa.jsx';

/* ═══════════════════════════════════════════════════════════
   Riscritto sul contratto reale (Collaudo_KM_TalentGPT_20260907_2.docx,
   Appendici D/E, verificato dal vivo il 18/09):

   - GET /resources/{id} risponde { resource_id, altamira_ref, anagrafica:
     {...}, profilo: {...}, self_assessment: [...], stato_ciclo_vita,
     fitting?: {...} }. stato_ciclo_vita sta alla radice, non annidato — e
     può essere ancora null per molte risorse (campo introdotto di recente).

   Link "Apri nuova esperienza" (18/09): dietro il parametro
   showApriEsperienza (default spento) — questo pannello è montato sia in
   MultifittingDashboard.jsx (contesto legato a un annuncio, dove aprire
   un'esperienza da qui creerebbe l'impressione sbagliata di un legame che
   non esiste nel dato: nessun jd_id in POST /esperienze/open) sia in
   CandidateEditModal.jsx (scheda della persona, senza legame con nessun
   annuncio — dove il link ha senso). I due genitori lo attivano in modo
   diverso, non questo file da solo.

   Generazione del link Self-Assessment (Canale WhatsApp/Email, "Genera
   link Self-Assessment") RIMOSSA il 27/09 — non funzionante, su richiesta
   esplicita. Il pannello resta di sola consultazione: stato, freschezza,
   storico istanze e apertura esperienza (dove previsto).

   INVIO SINGOLO — 27/09 (UC-SA-01, invio dal dettaglio risorsa): passa da
   POST /hr/invite/{jd_id} con resource_ids = [resource_id], lo stesso
   endpoint della D1, che applica lato server i controlli dell'AF (stato
   R2/R3, recapito, V22, profilo deciso dal sistema). Prima un'anteprima
   (dry_run=true) che dice se la risorsa verrebbe invitata o perché no,
   poi la conferma esplicita. Il backend oggi NON ha un invio singolo
   slegato da un annuncio (verificato: solo /hr/invite/{jd_id} e
   /hr/token, che genera un link senza controlli né invio): senza jdId
   (es. scheda candidato) il pannello lo dice invece di usare /hr/token.
   Invito già attivo: letto da /hr/inviti-storico (in_attesa, aperto),
   perché sa_in_corso resta falso finché il candidato non apre il link.
═══════════════════════════════════════════════════════════ */

// 16/10 — mostraConteggioEsperienze: il dettaglio unificato (ResourceDettaglio)
// ha già la sezione Esperienze con l'elenco, quindi lì il conteggio qui sotto
// sarebbe un doppione; gli altri punti d'ingresso lo mantengono.
export default function ResourceSelfAssessmentPanel({ resourceId, jdId, showApriEsperienza = false, onInvioInviato, mostraConteggioEsperienze = true }) {
  const kmClient = useRealKmClient();
  const navigate = useNavigate();
  const [promuoviStato, setPromuoviStato] = useState('idle'); // idle | loading | error
  const [promuoviErrore, setPromuoviErrore] = useState(null);

  const [status, setStatus] = useState('idle'); // idle | loading | ready | not_tracked | error
  const [resource, setResource] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [techDetails, setTechDetails] = useState(null);
  const [showTech, setShowTech] = useState(false);


  // Invio singolo (vedi nota in cima).
  const [invitoAttivo, setInvitoAttivo] = useState(null); // invito in_attesa/aperto più recente
  const [ultimoInvito, setUltimoInvito] = useState(null); // ultimo invito, qualunque stato (V32)
  const [invioFase, setInvioFase] = useState('idle'); // idle | preview_loading | preview | sending | sent | error
  const [invioAnteprima, setInvioAnteprima] = useState(null);
  const [invioErrore, setInvioErrore] = useState(null);

  // 13/10 — UAT-26, sollecito su un Self-Assessment già aperto (UC-SA-01,
  // flusso alternativo 4): POST /hr/risorse/{id}/sollecito-sa, dry_run=true di
  // default. Rimanda lo stesso link, senza nuova istanza e senza cambiare la
  // scadenza; il canale lo sceglie il backend (nessun corpo).
  const [sollecitoFase, setSollecitoFase] = useState('idle'); // idle | preview_loading | preview | sending | sent | error
  const [sollecitoAnteprima, setSollecitoAnteprima] = useState(null);
  const [sollecitoErrore, setSollecitoErrore] = useState(null);
  const [ultimoSollecito, setUltimoSollecito] = useState(null);
  // Un invio bloccato da V22 risponde esito "escluso" + sollecito_proponibile.
  const [sollecitoProponibile, setSollecitoProponibile] = useState(false);

  const loadResource = useCallback(() => {
    if (!resourceId) return;
    setStatus('loading');
    setErrorMessage(null);
    const requestUrl = jdId ? `/resources/${resourceId}?jd_id=${jdId}` : `/resources/${resourceId}`;
    kmClient
      .get(`/resources/${encodeURIComponent(resourceId)}`, jdId ? { query: { jd_id: jdId } } : undefined)
      .then((data) => {
        setResource(data);
        setTechDetails({ requestUrl, status: 200, note: 'Risposta ricevuta correttamente.' });
        setStatus('ready');
      })
      .catch((err) => {
        setTechDetails({
          requestUrl,
          status: err?.status ?? 'n/d',
          technicalMessage: err?.technicalMessage || null,
          detail: err?.detail || null,
          correlationId: err?.correlationId || null,
        });
        if (err?.status === 404) {
          setStatus('not_tracked');
        } else {
          setErrorMessage(err?.detail || 'Errore nel caricamento dello stato KM.');
          setStatus('error');
        }
      });
  }, [kmClient, resourceId, jdId]);

  useEffect(() => {
    setResource(null);
    setInvioFase('idle');
    setInvioAnteprima(null);
    setInvioErrore(null);
    setSollecitoFase('idle');
    setSollecitoAnteprima(null);
    setSollecitoErrore(null);
    setUltimoSollecito(null);
    setSollecitoProponibile(false);
    loadResource();
  }, [loadResource]);

  // candidato_id vero (uuid): resourceId può essere il cvid Altamira.
  const candidatoId = resource?.resource_id || null;

  // 28/09: una sola chiamata filtrata per risorsa (resource_id è un filtro
  // del backend) invece di due pagine globali filtrate in memoria, che
  // perdevano la risorsa oltre la duecentesima riga. Serve anche a V32:
  // l'ultimo invio, qualunque sia il suo stato.
  const caricaInvitoAttivo = useCallback(() => {
    if (!candidatoId) return Promise.resolve();
    return kmClient.get('/hr/inviti-storico', { query: { resource_id: candidatoId, limite: 50 } })
      .then((d) => (Array.isArray(d?.inviti) ? d.inviti : []))
      .catch(() => [])
      .then((lista) => {
        const miei = lista.filter((i) => !i?.resource_id || i.resource_id === candidatoId);
        miei.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setUltimoInvito(miei[0] || null);
        setInvitoAttivo(miei.find((i) => ['in_attesa', 'aperto'].includes(i.stato)) || null);
      });
  }, [kmClient, candidatoId]);

  useEffect(() => { caricaInvitoAttivo(); }, [caricaInvitoAttivo]);

  /* 28/09 — invio singolo da POST /hr/risorse/{id}/invito-sa (UC-SA-01,
     WF-CD-01): stesse precondizioni dell'invio dalla longlist, ma senza
     pretendere un annuncio. Se il pannello è aperto da un annuncio, l'invito
     vi resta legato (jd_id nel corpo). La risposta ({ esito, motivo, … })
     è ricondotta qui alla forma usata dal resto del pannello. */
  function inviteUrl(dryRun) {
    return `/hr/risorse/${encodeURIComponent(candidatoId)}/invito-sa?dry_run=${dryRun}`;
  }

  // 29/09 — canale: 'email' esplicito. Decisione di business: per ora solo
  // email, mai WhatsApp. Il default lato backend è "auto" (preferisce
  // WhatsApp se la risorsa ha un numero, V20) — richiesto ad Armando di
  // cambiarlo, ma finché non è fatto lo forziamo qui.
  function inviteBody() {
    return jdId ? { jd_id: String(jdId), canale: 'email' } : { canale: 'email' };
  }

  function leggiEsito(r) {
    const esito = r?.esito;
    const ok = esito === 'simulato' || esito === 'inviato';
    return {
      raw: r,
      inviati: ok ? 1 : 0,
      esclusi: ok ? [] : [{ motivo: r?.motivo || (esito === 'errore' ? 'consegna non riuscita' : null) }],
      profilo_sa: r?.profilo_sa || null,
      canale: r?.destinatario?.canale || r?.canale || null,
    };
  }

  async function handleAnteprimaInvio() {
    setInvioFase('preview_loading');
    setInvioErrore(null);
    try {
      const r = await kmClient.post(inviteUrl(true), inviteBody());
      if (r?.sollecito_proponibile) {
        setSollecitoProponibile(true);
        setInvioFase('idle');
        return;
      }
      setInvioAnteprima(leggiEsito(r));
      setInvioFase('preview');
    } catch (err) {
      setInvioErrore(err?.detail || "Errore durante la verifica dell'invio.");
      setInvioFase('error');
    }
  }

  async function handleConfermaInvio() {
    setInvioFase('sending');
    setInvioErrore(null);
    try {
      const r = await kmClient.post(inviteUrl(false), inviteBody());
      if (r?.sollecito_proponibile) {
        setSollecitoProponibile(true);
        setInvioAnteprima(null);
        setInvioFase('idle');
        return;
      }
      if (r?.esito !== 'inviato') {
        setInvioAnteprima(leggiEsito({ ...r, esito: r?.esito === 'simulato' ? 'errore' : r?.esito }));
        setInvioFase('preview');
        return;
      }
      setInvioFase('sent');
      setInvitoAttivo({ resource_id: candidatoId, stato: 'in_attesa', created_at: new Date().toISOString() });
      caricaInvitoAttivo();
      loadResource();
      // 30/09 — l'invio da qui non aggiornava "Stato ultimo SA"/la checkbox
      // nella riga della dashboard (MultifittingDashboard.jsx): il pannello
      // si limitava a ricaricare il proprio stato locale, non quello del
      // genitore. onInvioInviato avvisa chi ha montato il pannello, sullo
      // stesso schema già usato da ResourceStateManager/onStateChanged.
      if (typeof onInvioInviato === 'function') onInvioInviato();
    } catch (err) {
      setInvioErrore(err?.detail || "Errore durante l'invio.");
      setInvioFase('error');
    }
  }

  function sollecitoUrl(dryRun) {
    return `/hr/risorse/${encodeURIComponent(candidatoId)}/sollecito-sa?dry_run=${dryRun}`;
  }

  async function handleAnteprimaSollecito() {
    setSollecitoFase('preview_loading');
    setSollecitoErrore(null);
    try {
      const r = await kmClient.post(sollecitoUrl(true));
      setSollecitoAnteprima(leggiEsito(r));
      // La data dell'ultimo sollecito arriva dalla simulazione
      // (istanza_aperta.ultimo_sollecito); /resources/{id} oggi non la restituisce.
      if (r?.istanza_aperta?.ultimo_sollecito) setUltimoSollecito(r.istanza_aperta.ultimo_sollecito);
      setSollecitoFase('preview');
    } catch (err) {
      setSollecitoErrore(err?.detail || 'Errore durante la verifica del sollecito.');
      setSollecitoFase('error');
    }
  }

  async function handleConfermaSollecito() {
    setSollecitoFase('sending');
    setSollecitoErrore(null);
    try {
      const r = await kmClient.post(sollecitoUrl(false));
      if (r?.esito !== 'inviato') {
        // In caso di errore il link precedente resta valido (backend).
        setSollecitoAnteprima(leggiEsito({ ...r, esito: r?.esito === 'simulato' ? 'errore' : r?.esito }));
        setSollecitoFase('preview');
        return;
      }
      setUltimoSollecito(r?.istanza_aperta?.ultimo_sollecito || new Date().toISOString());
      setSollecitoFase('sent');
      caricaInvitoAttivo();
      loadResource();
    } catch (err) {
      setSollecitoErrore(err?.detail || 'Errore durante il sollecito.');
      setSollecitoFase('error');
    }
  }

  // stato_ciclo_vita sta alla radice della risposta (non annidato). Può
  // essere null anche per una risorsa esistente: campo introdotto di
  // recente, non ancora valorizzato per tutti.
  const cicloVita = resource?.stato_ciclo_vita ?? null;

  // Sottostato (solo per R3) — mai mostrato finora, pur arrivando sempre
  // nella risposta (v_risorsa_ciclo_vita.sottostato). Distingue "mai
  // staffato" da "attualmente staffato" da "ex-staffato": determina quale
  // profilo SA-2a/b/c riceverebbe la risorsa, non deducibile dal solo R3.
  const sottostato = resource?.sottostato ?? null;
  const stateLabel = statoRisorsaLabel(cicloVita, sottostato);

  // Freschezza Self-Assessment (Specifica v1.1 §5.3: "non confondere
  // aggiornamento recruiter e aggiornamento SA") — CORRETTO il 18/09 dopo
  // verifica diretta sul backend: avevo assunto che self_assessment[]
  // fosse popolato con lo storico delle compilazioni, ma su una risorsa
  // reale con una compilazione completata davvero nel DB, quell'array
  // torna vuoto. Il campo giusto è "freschezza_sa", un oggetto già pronto
  // e verificato popolato: { ultima_compilazione, giorni_dall_ultimo_sa,
  // soglia_giorni, freschezza_sa, sa_da_sollecitare, sa_in_corso }.
  //
  // "sa_da_sollecitare" è probabilmente il flag verso HR di cui parlava
  // Monaco (call 18/09) — più affidabile della mia ipotesi precedente
  // basata su self_assessment[].tipo, che si è rivelata vuota.
  const freschezza = resource?.freschezza_sa ?? null;
  const cicloVitaDettaglio = resource?.ciclo_vita ?? null;

  // self_assessment[] tenuto come fallback: non confermato se popolato in
  // altri casi (es. istanze aperte/in corso, diverse da quella completata
  // testata) — se vuoto semplicemente non mostra nulla, nessun dato finto.
  const saInstances = Array.isArray(resource?.self_assessment) ? resource.self_assessment : [];
  const saInstancesOrdinate = [...saInstances].sort((a, b) => {
    const da = new Date(a.closed_at || a.created_at || 0);
    const db = new Date(b.closed_at || b.created_at || 0);
    return db - da;
  });

  return (
    <div
      style={{
        marginTop: 16,
        padding: 14,
        borderRadius: 8,
        border: '1px dashed #185FA5',
        background: '#E6F1FB',
        fontSize: 13,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#0C447C', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        Self-Assessment
        {KM_CONFIG.HR_CONSOLE_MOCK_MODE && (
          <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#FAEEDA', color: '#633806', textTransform: 'none', letterSpacing: 0 }}>
            DATI DI SVILUPPO — non demo
          </span>
        )}
      </div>

      {status === 'loading' && <div style={{ color: '#6B6A65' }}>Verifica stato in corso…</div>}

      {status === 'not_tracked' && (
        <div>
          <div style={{ color: '#0C447C', fontWeight: 600, marginBottom: 6 }}>
            Non ancora collegata al modulo KM
          </div>
          <div style={{ color: '#3D3B36', marginBottom: 8 }}>
            Questa persona esiste nel bacino Altamira, ma non è (ancora) tracciata
            come candidato in TalentGPT — per questo qui non vedi stato, storico
            né la possibilità di inviare un Self-Assessment.
          </div>
          <div style={{ color: '#6B6A65', fontSize: 12 }}>
            È la situazione normale per la maggior parte delle persone in
            MultiFitting oggi, non un errore: il collegamento tra i due sistemi
            avviene solo per chi è già stato importato in TalentGPT.
          </div>
        </div>
      )}

      {status === 'error' && (
        <div>
          <div style={{ color: '#791F1F', marginBottom: 6 }}>{errorMessage}</div>
          <button type="button" onClick={loadResource} style={{ fontSize: 12, cursor: 'pointer' }}>
            Riprova
          </button>
        </div>
      )}

      {status === 'ready' && resource && (
        <div>
          <div style={{ marginBottom: 8 }}>
            Stato attuale:{' '}
            {stateLabel ? <strong>{stateLabel}</strong> : <span style={{ color: '#6B6A65' }}>non ancora valorizzato (N/D)</span>}
            {/* 12/10 — il badge di freschezza descrive la RISORSA (ultimo SA compilato
                rispetto alla soglia di validità), non la singola compilazione: sta
                sulla riga dello stato, non più accanto all'ultima istanza. */}
            {freschezza?.ultima_compilazione && ['aggiornato', 'scaduto'].includes(freschezza?.freschezza_sa) && (() => {
              const ok = freschezza.freschezza_sa === 'aggiornato';
              return (
                <span
                  className="badge"
                  title={ok
                    ? "L'ultimo Self-Assessment compilato rientra nella soglia di validità: il profilo non richiede aggiornamento."
                    : "L'ultimo Self-Assessment compilato è oltre la soglia di validità: il profilo va aggiornato."}
                  style={{
                    marginLeft: 8, padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'help',
                    background: ok ? '#EAF3DE' : '#FAEEDA',
                    color: ok ? '#27500A' : '#633806',
                  }}
                >
                  {ok ? 'Profilo aggiornato' : 'Profilo da aggiornare'}
                </span>
              );
            })()}
          </div>

          {/* 09/10 — SAL 02/10 azione 23: UN SOLO blocco Self-Assessment (prima:
              scheda freschezza + testo dentro "Invia"). Stato, date, avviso e
              azione stanno insieme. Logica di blocco invariata (V22, V32). */}
          {(() => {
            const fmt = (ts) => (ts ? new Date(ts).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
            const fmtData = (ts) => (ts ? new Date(ts).toLocaleDateString('it-IT') : '');
            const fa = (g) => (g == null ? '' : g === 0 ? ' (oggi)' : g === 1 ? ' (1 giorno fa)' : ` (${g} giorni fa)`);
            const inCorso = Boolean(freschezza?.sa_in_corso);
            const compilatoIl = freschezza?.ultima_compilazione || null;
            const invio = ultimoInvito || invitoAttivo || null;
            const dataInvio = invio?.created_at || null;
            const aperto = Boolean(invio?.consumed_at || invitoAttivo?.consumed_at);
            let statoSa = 'Non inviato';
            if (inCorso) statoSa = aperto ? 'In compilazione' : 'Inviato';
            else if (compilatoIl) statoSa = 'Compilato';
            else if (invio?.stato === 'scaduto') statoSa = 'Scaduto';
            else if (invio) statoSa = 'Inviato';
            const STILE = {
              'Non inviato': { background: '#F1EFE8', color: '#5F5E5A' },
              'Inviato': { background: '#D6E8F8', color: '#0C447C' },
              'In compilazione': { background: '#B5D4F4', color: '#0C447C' },
              'Compilato': { background: '#EAF3DE', color: '#27500A' },
              'Scaduto': { background: '#FCEBEB', color: '#791F1F' },
            };
            // 09/10 — elenco istanze: ogni questionario con il suo stato, così si
            // distingue quello completato da quello in compilazione.
            const ETICHETTA_ISTANZA = {
              completata: 'Compilato', completato: 'Compilato', chiusa: 'Compilato', chiuso: 'Compilato',
              in_corso: 'In compilazione', aperta: 'In compilazione', aperto: 'In compilazione',
              scaduta: 'Scaduto', scaduto: 'Scaduto', inviata: 'Inviato', inviato: 'Inviato',
            };
            // Nome leggibile del questionario (mai il codice SA-xx a schermo).
            const NOME_QUESTIONARIO = {
              SA1: 'Primo contatto (In qualifica)',
              SA2A: 'Risorsa proponibile, mai staffata',
              SA2B: 'Collaboratore attualmente staffato',
              SA2C: 'Collaboratore ex-staffato',
              SA4: 'Retrospettiva a fine progetto',
            };
            const nomeQuestionario = (t) => NOME_QUESTIONARIO[String(t || '').toUpperCase().replace(/[^A-Z0-9]/g, '')] || null;
            const etichettaIstanza = (st) => ETICHETTA_ISTANZA[String(st || '').toLowerCase()] || st || 'stato N/D';
            const righeDate = [
              dataInvio ? `Inviato il ${fmt(dataInvio)}${fa(giorniDallInvio(dataInvio))}` : 'Mai inviato',
              compilatoIl
                ? `Compilato il ${fmtData(compilatoIl)}${fa(freschezza?.giorni_dall_ultimo_sa)}`
                : (dataInvio ? 'Non ancora compilato' : 'Mai compilato'),
            ].join(' · ');
            const esclusi = Array.isArray(invioAnteprima?.esclusi) ? invioAnteprima.esclusi : [];
            const motivo = esclusi[0] ? (esclusi[0].motivo || esclusi[0].reason || esclusi[0].causa || JSON.stringify(esclusi[0])) : null;
            const ignoti = Array.isArray(invioAnteprima?.identificativi_ignoti) && invioAnteprima.identificativi_ignoti.length > 0;
            const invitabile = ['R2', 'R3'].includes(cicloVita);
            const recente = Boolean(ultimoInvito && invioRecente(ultimoInvito.created_at));
            // Una sola riga di avviso/blocco, in ordine di priorità.
            // 13/10 — UAT-26: un invito inviato e non ancora aperto (in_attesa) o un
            // SA in corso si possono solo sollecitare. Se l'istanza è già scaduta,
            // completata o il link è stato aperto senza risposte, decide il backend
            // (anteprima: esito "escluso" con il motivo).
            // 13/10 — GET /resources/{id} restituisce istanza_aperta ({ stato: Inviato |
            // In compilazione, inviato_il, scadenza_link, canale, ultimo_sollecito }), null
            // se non c'è un SA aperto: è il segnale principale; il resto resta di riserva.
            const istanzaAperta = resource?.istanza_aperta || null;
            const sollecitabile = Boolean(istanzaAperta) || inCorso || invitoAttivo?.stato === 'in_attesa' || sollecitoProponibile;
            const dataSollecito = ultimoSollecito
              || istanzaAperta?.ultimo_sollecito
              || null;
            const esclusiSol = Array.isArray(sollecitoAnteprima?.esclusi) ? sollecitoAnteprima.esclusi : [];
            const motivoSol = esclusiSol[0] ? (esclusiSol[0].motivo || esclusiSol[0].reason || esclusiSol[0].causa || null) : null;
            let avviso = null;
            let puoInviare = false;
            if (sollecitabile) avviso = { tono: 'info', testo: 'Un Self-Assessment è già stato inviato e non è ancora completato: non è possibile inviarne un altro. Puoi inviare un sollecito: la risorsa riceve un nuovo link che riapre lo stesso Self-Assessment, con le risposte già date.' };
            else if (!invitabile) avviso = { tono: 'muto', testo: 'Invio non disponibile: solo le risorse In qualifica o Proponibili possono ricevere un Self-Assessment.' };
            else if (invioFase === 'sent') avviso = { tono: 'ok', testo: '✓ Self-Assessment inviato.' };
            else if (recente) avviso = { tono: 'warn', testo: `Inviato da meno di ${SOGLIA_INVIO_RECENTE_GIORNI} giorni: per non sottoporre una seconda somministrazione ravvicinata si procede per contatto diretto.` };
            else puoInviare = true;
            const COL = { info: '#0C447C', muto: '#6B6A65', ok: '#27500A', warn: '#633806' };
            return (
              <div style={{ marginBottom: 10, fontSize: 12 }}>
                {saInstancesOrdinate.length === 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span className="badge" style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, ...STILE[statoSa] }}>{statoSa}</span>
                  </div>
                )}
                {saInstancesOrdinate.length > 0 ? (
                  <ul style={{ margin: '0 0 4px', paddingLeft: 0, listStyle: 'none', color: '#3D3B36' }}>
                    {saInstancesOrdinate.map((sa) => {
                      const et = etichettaIstanza(sa.stato);
                      const compilata = et === 'Compilato';
                      const quando = compilata && sa.closed_at
                        ? `il ${fmtData(sa.closed_at)}${fa(giorniDallInvio(sa.closed_at))}`
                        : sa.created_at
                          ? `inviato il ${fmt(sa.created_at)}${fa(giorniDallInvio(sa.created_at))}`
                          : '';
                      return (
                        <li key={sa.id} style={{ marginBottom: 3 }}>
                          {nomeQuestionario(sa.tipo) && <span>{nomeQuestionario(sa.tipo)} — </span>}
                          <span className="badge" style={{ padding: '1px 7px', borderRadius: 20, fontSize: 10.5, fontWeight: 600, ...(STILE[et] || STILE['Non inviato']) }}>{et}</span>
                          {quando ? <span> · {quando}</span> : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div style={{ color: '#3D3B36', marginBottom: 4 }}>{righeDate}</div>
                )}
                {freschezza?.sa_da_sollecitare && !inCorso && (
                  <div style={{ color: '#633806', fontWeight: 600, marginBottom: 4 }}>⚠ Da sollecitare</div>
                )}
                {avviso && <div style={{ color: COL[avviso.tono], fontWeight: avviso.tono === 'ok' ? 600 : 400 }}>{avviso.testo}</div>}
                {sollecitabile && (
                  <div style={{ color: '#3D3B36', marginTop: 4 }}>
                    {dataSollecito
                      ? <>Ultimo sollecito il {fmt(dataSollecito)}{fa(giorniDallInvio(dataSollecito))}</>
                      : (istanzaAperta ? 'Nessun sollecito inviato finora' : null)}
                    {istanzaAperta?.scadenza_link && (
                      <div>Link valido fino al {fmtData(istanzaAperta.scadenza_link)}</div>
                    )}
                  </div>
                )}
                {sollecitabile && (
                  <div style={{ marginTop: 6 }}>
                    {sollecitoFase === 'sent' && (
                      <div style={{ marginBottom: 6, color: '#27500A', fontWeight: 600 }}>✓ Sollecito inviato: nuovo link sullo stesso Self-Assessment, scadenza invariata.</div>
                    )}
                    {sollecitoFase === 'preview' && sollecitoAnteprima && (
                      sollecitoAnteprima.inviati ? (
                        <div style={{ marginBottom: 6 }}>
                          Verifica superata: la risorsa riceverà un nuovo link che riapre il Self-Assessment aperto, con le risposte già date,
                          {sollecitoAnteprima.canale ? ` su ${sollecitoAnteprima.canale === 'whatsapp' ? 'WhatsApp' : 'posta elettronica'}` : ''}.
                          La scadenza non cambia e il link precedente non sarà più valido. Confermi il sollecito?
                        </div>
                      ) : (
                        <div style={{ marginBottom: 6, color: '#791F1F' }}>
                          Sollecito non possibile{motivoSol ? `: ${motivoSol}` : '.'}
                        </div>
                      )
                    )}
                    {sollecitoErrore && <div style={{ marginBottom: 6, color: '#791F1F' }}>⚠ {sollecitoErrore}</div>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!(sollecitoFase === 'preview' && sollecitoAnteprima?.inviati) ? (
                        <button
                          type="button"
                          onClick={handleAnteprimaSollecito}
                          disabled={sollecitoFase === 'preview_loading' || !candidatoId}
                          style={{ padding: '5px 12px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.2)', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          {sollecitoFase === 'preview_loading' ? 'Verifica in corso…' : 'Invia sollecito'}
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={handleConfermaSollecito}
                            disabled={sollecitoFase === 'sending'}
                            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          >
                            {sollecitoFase === 'sending' ? 'Invio in corso…' : 'Conferma sollecito'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setSollecitoFase('idle'); setSollecitoAnteprima(null); }}
                            disabled={sollecitoFase === 'sending'}
                            style={{ padding: '5px 12px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.2)', background: '#fff', fontSize: 12, cursor: 'pointer' }}
                          >
                            Annulla
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {puoInviare && (
                  <div style={{ marginTop: 6 }}>
                    {invioFase === 'preview' && invioAnteprima && (
                      invioAnteprima.inviati ? (
                        <div style={{ marginBottom: 6 }}>
                          Verifica superata: il sistema invierà il questionario corrispondente allo stato della risorsa
                          {invioAnteprima.canale ? ` su ${invioAnteprima.canale === 'whatsapp' ? 'WhatsApp' : 'posta elettronica'}` : ''}. Confermi l'invio?
                        </div>
                      ) : (
                        <div style={{ marginBottom: 6, color: '#791F1F' }}>
                          Invio non possibile{motivo ? `: ${motivo}` : ignoti ? ': risorsa non riconosciuta' : '.'}
                        </div>
                      )
                    )}
                    {invioErrore && <div style={{ marginBottom: 6, color: '#791F1F' }}>⚠ {invioErrore}</div>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!(invioFase === 'preview' && invioAnteprima?.inviati) ? (
                        <button
                          type="button"
                          onClick={handleAnteprimaInvio}
                          disabled={invioFase === 'preview_loading' || !candidatoId}
                          style={{ padding: '5px 12px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.2)', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          {invioFase === 'preview_loading' ? 'Verifica in corso…' : 'Invia Self-Assessment'}
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={handleConfermaInvio}
                            disabled={invioFase === 'sending'}
                            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          >
                            {invioFase === 'sending' ? 'Invio in corso…' : 'Conferma invio'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setInvioFase('idle'); setInvioAnteprima(null); }}
                            disabled={invioFase === 'sending'}
                            style={{ padding: '5px 12px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.2)', background: '#fff', fontSize: 12, cursor: 'pointer' }}
                          >
                            Annulla
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {mostraConteggioEsperienze && cicloVitaDettaglio && (cicloVitaDettaglio.esperienze_attive > 0 || cicloVitaDettaglio.esperienze_totali > 0) && (
            <div style={{ marginBottom: 10, fontSize: 11.5, color: '#6B6A65' }}>
              Esperienze: {cicloVitaDettaglio.esperienze_attive} attive su {cicloVitaDettaglio.esperienze_totali} totali
            </div>
          )}

          {/* UAT-16: certificazioni, settori e domini dichiarati, con i nomi (mai i codici). */}
          <ResourceProfiloSa resource={resource} />

          {showApriEsperienza && resource?.stato_ciclo_vita === 'R3' && (
            <div style={{ marginBottom: 10 }}>
              {/* Corretto il 22/09: prima passava resourceId (candidato) come
                  consulente_id — confermato sbagliato (candidato.altamira_ref
                  e consulente.altamira_ref non si toccavano mai). Ora chiama
                  POST /resources/{id}/consulente (Monaco, 22/09 — verificato
                  da noi: crea/recupera il consulente vero, idempotente) e
                  naviga solo con l'id restituito da lì. Richiede R3 (409
                  altrimenti) — per questo il bottone compare solo in quello
                  stato, non genericamente come prima. */}
              <button
                type="button"
                onClick={async () => {
                  setPromuoviStato('loading');
                  setPromuoviErrore(null);
                  try {
                    const promosso = await kmClient.post(`/resources/${encodeURIComponent(resourceId)}/consulente`);
                    navigate(`/knowledge-management/esperienze/nuova?consulente_id=${encodeURIComponent(promosso.consulente_id)}`);
                  } catch (err) {
                    setPromuoviErrore(err?.detail || 'Errore nella creazione dell’anagrafica consulente.');
                    setPromuoviStato('error');
                  }
                }}
                disabled={promuoviStato === 'loading'}
                className="btn-link"
                style={{ fontSize: 12, background: 'none', border: 'none', padding: 0, cursor: promuoviStato === 'loading' ? 'default' : 'pointer', textDecoration: 'underline' }}
              >
                {promuoviStato === 'loading' ? 'Preparazione in corso…' : '+ Apri nuova esperienza per questa risorsa'}
              </button>
              {promuoviStato === 'error' && (
                <div style={{ fontSize: 11, color: '#791F1F', marginTop: 4 }}>⚠ {promuoviErrore}</div>
              )}
            </div>
          )}

          {/* Fallback: self_assessment[] tenuto per compatibilità, ma
              verificato vuoto anche su una risorsa con compilazione reale
              completata — probabilmente non affidabile come fonte
              principale, vedi nota sopra. */}
        </div>
      )}

      {techDetails && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setShowTech((v) => !v)}
            style={{ fontSize: 11, color: '#0C447C', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            {showTech ? '▲ Nascondi dettagli tecnici' : '▼ Mostra dettagli tecnici'}
          </button>
          {showTech && (
            <div style={{ marginTop: 6, padding: 8, borderRadius: 6, background: '#fff', border: '0.5px solid rgba(0,0,0,0.12)', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              URL: {techDetails.requestUrl}{'\n'}
              Status: {techDetails.status}{'\n'}
              {techDetails.technicalMessage && `Messaggio tecnico: ${techDetails.technicalMessage}\n`}
              {techDetails.detail && `Detail backend: ${techDetails.detail}\n`}
              {techDetails.correlationId && `Correlation id: ${techDetails.correlationId}\n`}
              {techDetails.note && `${techDetails.note}\n`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
